import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  GearSix,
  Crosshair,
  Plus,
  Trash,
  Tag,
  Globe,
  ShieldCheck,
  ArrowSquareOut,
  Megaphone,
  PencilSimple,
  Eye,
  ArrowsClockwise,
  Play,
  Code,
  Copy,
  Check,
  Broadcast,
  Sparkle,
  CircleNotch,
  Lightning,
  Link as LinkIcon,
  CheckCircle,
  WarningCircle,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import '../styles/design-system.css';
import '../styles/pages.css';
import './SettingsPage.css';

export default function SettingsPage() {
  const { workspace, setWorkspace } = useAuth();
  const [wsName, setWsName] = useState('');
  const [wsTimezone, setWsTimezone] = useState('UTC');
  const [privacyMode, setPrivacyMode] = useState(false);

  const [pixels, setPixels] = useState([]);
  const [showAddPixel, setShowAddPixel] = useState(false);
  const [pixelForm, setPixelForm] = useState({ name: '', type: 'meta', pixel_code: '' });
  const [editingPixel, setEditingPixel] = useState(null);
  const [editPixelForm, setEditPixelForm] = useState({ name: '', pixel_code: '' });

  const [utmPresets, setUtmPresets] = useState([]);
  const [showAddUTM, setShowAddUTM] = useState(false);
  const [utmForm, setUtmForm] = useState({ name: '', source: '', medium: '', campaign: '', term: '', content: '' });
  const [editingUTM, setEditingUTM] = useState(null);
  const [editUTMForm, setEditUTMForm] = useState({ name: '', source: '', medium: '', campaign: '', term: '', content: '' });

  const [domains, setDomains] = useState([]);
  const [domainInput, setDomainInput] = useState('');
  const [lastAddedDomain, setLastAddedDomain] = useState(null);

  const [billing, setBilling] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);

  const [webhooks, setWebhooks] = useState([]);
  const [webhookForm, setWebhookForm] = useState({ url: '', events: [] });
  const [webhookLoading, setWebhookLoading] = useState(false);

  // Audit log
  const [auditEvents, setAuditEvents] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditCursor, setAuditCursor] = useState(null);
  const [auditHasMore, setAuditHasMore] = useState(false);

  // API Keys
  const [apiKeys, setApiKeys] = useState([]);
  const [apiKeysLoading, setApiKeysLoading] = useState(false);
  const [showCreateKey, setShowCreateKey] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyScopes, setNewKeyScopes] = useState(['links:read', 'links:write', 'analytics:read']);
  const [createdKey, setCreatedKey] = useState(null);
  const [keyCopied, setKeyCopied] = useState(false);

  const [ctas, setCtas] = useState([]);
  const [ctaStats, setCtaStats] = useState([]);
  const [showAddCTA, setShowAddCTA] = useState(false);
  const [ctaForm, setCtaForm] = useState({
    name: '', headline: '', body_text: '', button_label: 'Learn More', button_url: '',
    position: 'bottom_bar', style: { bg_color: '#131b2e', text_color: '#FFFFFF', button_color: '#4648d4', button_text_color: '#FFFFFF' },
  });
  const [ctaAiLoading, setCtaAiLoading] = useState(false);

  // CAPI (server-side conversions)
  const [capiSettings, setCapiSettings] = useState(null);
  const [capiLoading, setCapiLoading] = useState(false);
  const [capiSaving, setCapiSaving] = useState(false);
  const [capiForm, setCapiForm] = useState({
    enabled_meta: false, meta_pixel_id: '', meta_access_token: '', meta_test_event_code: '',
    enabled_ga4: false, ga4_measurement_id: '', ga4_api_secret: '',
  });
  const [capiTestResult, setCapiTestResult] = useState(null);
  const [capiTestLoading, setCapiTestLoading] = useState(false);
  const [capiSnippetCopied, setCapiSnippetCopied] = useState(false);

  const fetchAuditLog = useCallback(async (cursor = null, append = false) => {
    if (!workspace) return;
    setAuditLoading(true);
    try {
      const qs = cursor ? `?cursor=${cursor}` : '';
      const res = await api.get(`/audit${qs}`);
      const events = res.data?.data || res.data || [];
      const meta = res.data?.meta || {};
      setAuditEvents(prev => append ? [...prev, ...events] : events);
      setAuditCursor(meta.cursor || null);
      setAuditHasMore(!!meta.cursor);
    } catch (err) {
      console.error('Failed to load audit log', err);
    } finally {
      setAuditLoading(false);
    }
  }, [workspace]);

  const fetchCapiSettings = useCallback(async () => {
    if (!workspace) return;
    setCapiLoading(true);
    try {
      const res = await api.get('/capi/settings');
      if (res.data) {
        setCapiSettings(res.data);
        setCapiForm({
          enabled_meta: res.data.enabled_meta || false,
          meta_pixel_id: res.data.meta_pixel_id || '',
          meta_access_token: res.data.meta_access_token || '',
          meta_test_event_code: res.data.meta_test_event_code || '',
          enabled_ga4: res.data.enabled_ga4 || false,
          ga4_measurement_id: res.data.ga4_measurement_id || '',
          ga4_api_secret: res.data.ga4_api_secret || '',
        });
      }
    } catch (err) {
      console.error('Failed to load CAPI settings', err);
    } finally {
      setCapiLoading(false);
    }
  }, [workspace]);

  const handleSaveCapiSettings = async () => {
    setCapiSaving(true);
    try {
      await api.put('/capi/settings', capiForm);
      toast.success('Conversion settings saved');
      fetchCapiSettings();
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setCapiSaving(false);
    }
  };

  const handleCapiTest = async () => {
    setCapiTestLoading(true);
    setCapiTestResult(null);
    try {
      const res = await api.post('/capi/test', { event_name: 'TestEvent', value: 10, currency: 'USD' });
      setCapiTestResult(res.data);
      toast.success('Test event sent');
    } catch (err) {
      toast.error('Test event failed');
      setCapiTestResult({ error: err.message });
    } finally {
      setCapiTestLoading(false);
    }
  };

  const fetchApiKeys = useCallback(async () => {
    if (!workspace) return;
    setApiKeysLoading(true);
    try {
      const res = await api.get('/api-keys');
      setApiKeys(res.data || []);
    } catch (err) {
      console.error('Failed to load API keys', err);
    } finally {
      setApiKeysLoading(false);
    }
  }, [workspace]);

  const handleCreateApiKey = async () => {
    if (!newKeyName.trim()) return toast.error('Key name is required');
    try {
      const res = await api.post('/api-keys', { name: newKeyName, scopes: newKeyScopes });
      setCreatedKey(res.data.key);
      setNewKeyName('');
      setNewKeyScopes(['links:read', 'links:write', 'analytics:read']);
      fetchApiKeys();
    } catch (err) {
      toast.error(err.message || 'Failed to create API key');
    }
  };

  const handleRevokeApiKey = async (keyId) => {
    if (!window.confirm('Revoke this API key? Any apps using it will lose access immediately.')) return;
    try {
      await api.delete(`/api-keys/${keyId}`);
      setApiKeys(prev => prev.filter(k => k.key_id !== keyId));
      toast.success('API key revoked');
    } catch {
      toast.error('Failed to revoke key');
    }
  };

  const copyKey = () => {
    navigator.clipboard.writeText(createdKey);
    setKeyCopied(true);
    setTimeout(() => setKeyCopied(false), 2000);
  };

  const fetchPixels = useCallback(async () => {
    if (!workspace) return;
    try {
      const res = await api.get(`/workspaces/${workspace.workspace_id}/pixels`);
      setPixels(res.data);
    } catch (err) {
      console.error('Failed to load pixels', err);
    }
  }, [workspace]);

  const fetchUTMPresets = useCallback(async () => {
    if (!workspace) return;
    try {
      const res = await api.get(`/workspaces/${workspace.workspace_id}/utm-presets`);
      setUtmPresets(res.data);
    } catch (err) {
      console.error('Failed to load UTM presets', err);
    }
  }, [workspace]);

  const fetchCTAs = useCallback(async () => {
    if (!workspace) return;
    try {
      const [ctaRes, statsRes] = await Promise.all([
        api.get(`/workspaces/${workspace.workspace_id}/ctas`),
        api.get(`/workspaces/${workspace.workspace_id}/cta-stats`),
      ]);
      setCtas(ctaRes.data);
      setCtaStats(statsRes.data);
    } catch (err) {
      console.error('Failed to load CTAs', err);
    }
  }, [workspace]);

  const handleGenerateCtaWithAi = async () => {
    if (!ctaForm.button_url?.trim()) return toast.error('Enter the button URL first so AI can read the page');
    setCtaAiLoading(true);
    try {
      const res = await api.post('/ai/generate-cta', { destination_url: ctaForm.button_url });
      const ai = res.data;
      setCtaForm((prev) => ({
        ...prev,
        name: ai.name || prev.name,
        headline: ai.headline || prev.headline,
        body_text: ai.body_text !== undefined ? ai.body_text : prev.body_text,
        button_label: ai.button_label || prev.button_label,
      }));
      toast.success('AI copy applied');
    } catch {
      toast.error('AI generation failed');
    } finally {
      setCtaAiLoading(false);
    }
  };

  const handleAddCTA = async () => {
    try {
      await api.post(`/workspaces/${workspace.workspace_id}/ctas`, ctaForm);
      setShowAddCTA(false);
      setCtaForm({ name: '', headline: '', body_text: '', button_label: 'Learn More', button_url: '', position: 'bottom_bar', style: { bg_color: '#131b2e', text_color: '#FFFFFF', button_color: '#4648d4', button_text_color: '#FFFFFF' } });
      fetchCTAs();
      toast.success('CTA overlay created');
    } catch (err) {
      toast.error('Failed to create CTA');
    }
  };

  const handleDeleteCTA = async (ctaId) => {
    if (!window.confirm('Delete this CTA overlay?')) return;
    try {
      await api.delete(`/workspaces/${workspace.workspace_id}/ctas/${ctaId}`);
      fetchCTAs();
      toast.success('CTA deleted');
    } catch (err) {
      toast.error('Failed to delete CTA');
    }
  };

  const handleSaveWorkspace = async () => {
    try {
      const res = await api.put(`/workspaces/${workspace.workspace_id}`, {
        name: wsName,
        settings: { timezone: wsTimezone, privacy_mode: privacyMode },
      });
      setWorkspace(res.data);
      toast.success('Workspace settings saved');
    } catch {
      toast.error('Failed to save settings');
    }
  };

  const handleAddPixel = async () => {
    try {
      await api.post(`/workspaces/${workspace.workspace_id}/pixels`, pixelForm);
      setShowAddPixel(false);
      setPixelForm({ name: '', type: 'meta', pixel_code: '' });
      fetchPixels();
      toast.success('Pixel added');
    } catch {
      toast.error('Failed to add pixel');
    }
  };

  const handleDeletePixel = async (pixelId) => {
    if (!window.confirm('Delete this pixel?')) return;
    try {
      await api.delete(`/workspaces/${workspace.workspace_id}/pixels/${pixelId}`);
      fetchPixels();
      toast.success('Pixel deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleEditPixel = (px) => {
    setEditingPixel(px);
    setEditPixelForm({ name: px.name, pixel_code: px.pixel_code });
  };

  const handleSaveEditPixel = async () => {
    try {
      await api.put(`/workspaces/${workspace.workspace_id}/pixels/${editingPixel.pixel_id}`, editPixelForm);
      setEditingPixel(null);
      fetchPixels();
      toast.success('Pixel updated');
    } catch {
      toast.error('Failed to update pixel');
    }
  };

  const handleAddUTM = async () => {
    try {
      await api.post(`/workspaces/${workspace.workspace_id}/utm-presets`, utmForm);
      setShowAddUTM(false);
      setUtmForm({ name: '', source: '', medium: '', campaign: '', term: '', content: '' });
      fetchUTMPresets();
      toast.success('UTM preset created');
    } catch {
      toast.error('Failed to create preset');
    }
  };

  const handleDeleteUTM = async (presetId) => {
    if (!window.confirm('Delete this UTM preset?')) return;
    try {
      await api.delete(`/workspaces/${workspace.workspace_id}/utm-presets/${presetId}`);
      fetchUTMPresets();
      toast.success('Preset deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleEditUTM = (preset) => {
    setEditingUTM(preset);
    setEditUTMForm({ name: preset.name, source: preset.source, medium: preset.medium, campaign: preset.campaign, term: preset.term, content: preset.content });
  };

  const handleSaveEditUTM = async () => {
    try {
      await api.put(`/workspaces/${workspace.workspace_id}/utm-presets/${editingUTM.preset_id}`, editUTMForm);
      setEditingUTM(null);
      fetchUTMPresets();
      toast.success('UTM preset updated');
    } catch {
      toast.error('Failed to update preset');
    }
  };

  const fetchBillingStatus = useCallback(async () => {
    if (!workspace) return;
    setBillingLoading(true);
    try {
      const res = await api.get(`/billing/status?workspace_id=${workspace.workspace_id}`);
      setBilling(res.data);
    } catch (err) {
      console.error('Failed to load billing status', err);
    } finally {
      setBillingLoading(false);
    }
  }, [workspace]);

  const handleUpgrade = async (plan) => {
    try {
      const res = await api.post('/billing/checkout', { plan, workspace_id: workspace.workspace_id });
      window.location.href = res.data.session_url;
    } catch {
      toast.error('Failed to start checkout');
    }
  };

  const handleManageBilling = async () => {
    try {
      const res = await api.post('/billing/portal', { workspace_id: workspace.workspace_id });
      window.location.href = res.data.portal_url;
    } catch {
      toast.error('Failed to open billing portal');
    }
  };

  const fetchDomains = useCallback(async () => {
    if (!workspace) return;
    try {
      const res = await api.get(`/workspaces/${workspace.workspace_id}/domains`);
      setDomains(res.data);
    } catch (err) {
      console.error('Failed to load domains', err);
    }
  }, [workspace]);

  const handleAddDomain = async () => {
    const trimmed = domainInput.trim().toLowerCase();
    if (!trimmed) return;
    try {
      const res = await api.post(`/workspaces/${workspace.workspace_id}/domains`, { domain: trimmed });
      setLastAddedDomain(res.data);
      setDomainInput('');
      fetchDomains();
      toast.success('Domain registered');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to register domain');
    }
  };

  const handleVerifyDomain = async (domainId) => {
    try {
      const res = await api.post(`/workspaces/${workspace.workspace_id}/domains/${domainId}/verify`);
      fetchDomains();
      if (res.data.verified) {
        toast.success('Domain verified successfully');
      } else {
        toast.warning('CNAME not detected yet — DNS changes can take up to 48 hours');
      }
    } catch {
      toast.error('Verification check failed');
    }
  };

  const handleDeleteDomain = async (domainId) => {
    if (!window.confirm('Delete this domain?')) return;
    try {
      await api.delete(`/workspaces/${workspace.workspace_id}/domains/${domainId}`);
      setDomains((prev) => prev.filter((d) => d.domain_id !== domainId));
      if (lastAddedDomain?.domain_id === domainId) setLastAddedDomain(null);
      toast.success('Domain deleted');
    } catch {
      toast.error('Failed to delete domain');
    }
  };

  const fetchWebhooks = useCallback(async () => {
    if (!workspace) return;
    try {
      const res = await api.get(`/workspaces/${workspace.workspace_id}/webhooks`);
      setWebhooks(res.data);
    } catch (err) {
      console.error('Failed to load webhooks', err);
    }
  }, [workspace]);

  useEffect(() => {
    if (workspace) {
      setWsName(workspace.name || '');
      setWsTimezone(workspace.settings?.timezone || 'UTC');
      setPrivacyMode(workspace.settings?.privacy_mode || false);
      fetchPixels();
      fetchUTMPresets();
      fetchCTAs();
      fetchDomains();
      fetchBillingStatus();
      fetchWebhooks();
      fetchApiKeys();
      fetchAuditLog();
      fetchCapiSettings();
    }
  }, [workspace, fetchPixels, fetchUTMPresets, fetchCTAs, fetchDomains, fetchBillingStatus, fetchWebhooks, fetchApiKeys, fetchAuditLog, fetchCapiSettings]);

  const handleAddWebhook = async () => {
    if (!webhookForm.url.trim()) { toast.error('URL is required'); return; }
    if (webhookForm.events.length === 0) { toast.error('Select at least one event'); return; }
    setWebhookLoading(true);
    try {
      await api.post(`/workspaces/${workspace.workspace_id}/webhooks`, webhookForm);
      setWebhookForm({ url: '', events: [] });
      fetchWebhooks();
      toast.success('Webhook registered');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to register webhook');
    } finally {
      setWebhookLoading(false);
    }
  };

  const handleDeleteWebhook = async (webhookId) => {
    if (!window.confirm('Delete this webhook?')) return;
    try {
      await api.delete(`/workspaces/${workspace.workspace_id}/webhooks/${webhookId}`);
      setWebhooks((prev) => prev.filter((w) => w.webhook_id !== webhookId));
      toast.success('Webhook deleted');
    } catch {
      toast.error('Failed to delete webhook');
    }
  };

  const handleTestWebhook = async (webhookId) => {
    try {
      await api.post(`/workspaces/${workspace.workspace_id}/webhooks/${webhookId}/test`);
      toast.success('Test payload sent');
    } catch {
      toast.error('Failed to send test payload');
    }
  };

  const toggleWebhookEvent = (event) => {
    setWebhookForm((prev) => ({
      ...prev,
      events: prev.events.includes(event)
        ? prev.events.filter((e) => e !== event)
        : [...prev.events, event],
    }));
  };

  const WEBHOOK_EVENTS = ['link.clicked', 'link.created'];

  const pixelTypeLabels = { meta: 'Meta Pixel', google: 'Google Ads', linkedin: 'LinkedIn Insight', tiktok: 'TikTok Pixel' };

  return (
    <div className="ds-page lfs-root" data-testid="settings-page">
      <div className="page-header">
        <div>
          <div className="page-label olive">Workspace · {workspace?.name || 'your workspace'}</div>
          <h1 className="page-title">Tune your <em>workspace</em>.</h1>
          <p className="page-sub">
            Profile, billing, integrations, and team — all in one place.
          </p>
        </div>
      </div>

      <Tabs defaultValue="workspace" className="lfs-tabs">
        <TabsList className="lfs-nav">
          <TabsTrigger value="workspace" className="lfs-nav-item">
            <GearSix size={14} className="mr-1.5" /> Workspace
          </TabsTrigger>
          <TabsTrigger value="pixels" className="lfs-nav-item">
            <Crosshair size={14} className="mr-1.5" /> Pixels
          </TabsTrigger>
          <TabsTrigger value="utm" className="lfs-nav-item">
            <Tag size={14} className="mr-1.5" /> UTM presets
          </TabsTrigger>
          <TabsTrigger value="ctas" className="lfs-nav-item">
            <Megaphone size={14} className="mr-1.5" /> CTA overlays
          </TabsTrigger>
          <TabsTrigger value="domains" className="lfs-nav-item" data-testid="domains-tab">
            <Globe size={14} className="mr-1.5" /> Domains
          </TabsTrigger>
          <TabsTrigger value="webhooks" className="lfs-nav-item" data-testid="webhooks-tab">
            <ArrowsClockwise size={14} className="mr-1.5" /> Webhooks
          </TabsTrigger>
          <TabsTrigger value="api" className="lfs-nav-item" data-testid="api-tab">
            <Code size={14} className="mr-1.5" /> API Keys
          </TabsTrigger>
          <TabsTrigger value="audit" className="lfs-nav-item" data-testid="audit-tab">
            <ShieldCheck size={14} className="mr-1.5" /> Audit Log
          </TabsTrigger>
          <TabsTrigger value="conversions" className="lfs-nav-item" data-testid="conversions-tab">
            <Broadcast size={14} className="mr-1.5" /> Conversions
          </TabsTrigger>
          <TabsTrigger value="zapier" className="lfs-nav-item" data-testid="zapier-tab">
            <Lightning size={14} className="mr-1.5" /> Zapier
          </TabsTrigger>
        </TabsList>

        {/* Workspace tab */}
        <TabsContent value="workspace">
          <div className="surface-card ambient-shadow border-border/90 rounded-xl p-8 max-w-2xl space-y-6">
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-2 block">
                Workspace name
              </label>
              <input
                value={wsName}
                onChange={(e) => setWsName(e.target.value)}
                className="w-full rounded-lg border border-border bg-white px-4 py-3 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-shadow"
                data-testid="workspace-name-input"
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-2 block">
                Timezone
              </label>
              <Select value={wsTimezone} onValueChange={setWsTimezone}>
                <SelectTrigger className="bg-white rounded-lg border-border h-11" data-testid="timezone-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {['UTC', 'America/New_York', 'America/Chicago', 'America/Los_Angeles', 'Europe/London', 'Europe/Berlin', 'Europe/Paris', 'Asia/Tokyo', 'Asia/Shanghai', 'Australia/Sydney'].map((tz) => (
                    <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between gap-4 rounded-xl border border-border/80 bg-muted/20 px-4 py-4">
              <div>
                <p className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <ShieldCheck size={18} weight="duotone" className="text-primary" />
                  Privacy mode (GDPR)
                </p>
                <p className="text-xs text-muted-foreground mt-1">Cookieless tracking, no raw IP storage</p>
              </div>
              <Switch checked={privacyMode} onCheckedChange={setPrivacyMode} data-testid="privacy-mode-toggle" />
            </div>

            {/* Billing section */}
            {(() => {
              const plan = billing?.plan || 'free';
              const isPaid = plan === 'pro' || plan === 'business';
              const clicksUsed = billing?.clicks_used ?? 0;
              const clicksLimit = billing?.clicks_limit ?? 5000;
              const usagePct = clicksLimit > 0 ? Math.min(100, Math.round((clicksUsed / clicksLimit) * 100)) : 0;
              const planColors = {
                free: { bg: '#F3F4F6', color: '#1A1A1A' },
                pro: { bg: '#dcfce7', color: '#2d6a3f' },
                business: { bg: '#fef3c7', color: '#b87c1a' },
              };
              const pc = planColors[plan] || planColors.free;
              const expiresAt = billing?.plan_expires_at;

              return (
                <div className="rounded-xl border border-border/80 bg-primary/[0.03] p-5" data-testid="billing-section">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Current plan</p>
                    <span
                      className="text-xs font-semibold px-2.5 py-1 rounded-full"
                      style={{ background: pc.bg, color: pc.color }}
                      data-testid="plan-badge"
                    >
                      {plan.charAt(0).toUpperCase() + plan.slice(1)}
                    </span>
                  </div>

                  {expiresAt && (
                    <p className="text-xs text-muted-foreground mb-3">
                      Renews {new Date(expiresAt).toLocaleDateString()}
                    </p>
                  )}

                  <div className="mb-4" data-testid="usage-meter">
                    <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
                      <span>Clicks this month</span>
                      <span className="font-medium text-foreground tabular-nums">{clicksUsed.toLocaleString()} / {clicksLimit.toLocaleString()}</span>
                    </div>
                    <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          width: `${usagePct}%`,
                          background: usagePct >= 90 ? 'hsl(var(--destructive))' : usagePct >= 70 ? '#b87c1a' : '#4648d4',
                        }}
                        data-testid="usage-bar"
                      />
                    </div>
                  </div>

                  {billingLoading ? (
                    <p className="text-xs text-muted-foreground">Loading billing info…</p>
                  ) : isPaid ? (
                    <Button
                      variant="outline"
                      className="rounded-lg text-sm gap-1.5 border-border"
                      onClick={handleManageBilling}
                      data-testid="manage-billing-btn"
                    >
                      <ArrowSquareOut size={13} /> Manage billing
                    </Button>
                  ) : (
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        variant="outline"
                        className="rounded-lg text-sm gap-1.5 border-border"
                        onClick={() => handleUpgrade('pro')}
                        data-testid="upgrade-pro-btn"
                      >
                        <ArrowSquareOut size={13} /> Upgrade to Pro
                      </Button>
                      <Button
                        variant="outline"
                        className="rounded-lg text-sm gap-1.5 border-border"
                        onClick={() => handleUpgrade('business')}
                        data-testid="upgrade-business-btn"
                      >
                        <ArrowSquareOut size={13} /> Upgrade to Business
                      </Button>
                    </div>
                  )}
                </div>
              );
            })()}

            <Button onClick={handleSaveWorkspace} className="rounded-lg bg-primary text-primary-foreground hover:opacity-95 font-semibold px-8 h-11 ambient-shadow" data-testid="save-workspace-btn">
              Save changes
            </Button>
          </div>
        </TabsContent>

        {/* Retargeting Pixels tab */}
        <TabsContent value="pixels">
          <div className="surface-card ambient-shadow border-border/90 rounded-xl p-6 md:p-8">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
              <div>
                <h3 className="font-display text-lg font-bold text-foreground tracking-tight">Retargeting pixels</h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-xl">Fire tracking pixels when links are clicked.</p>
              </div>
              <Button onClick={() => setShowAddPixel(true)} className="rounded-lg bg-primary text-primary-foreground hover:opacity-95 text-sm gap-1.5 h-10 font-semibold shrink-0" data-testid="add-pixel-btn">
                <Plus size={16} weight="bold" /> Add pixel
              </Button>
            </div>
            {pixels.length > 0 ? (
              <div className="space-y-3">
                {pixels.map((px) => (
                  <div key={px.pixel_id} className="flex items-center justify-between rounded-xl border border-border/80 bg-white p-4 transition-colors hover:border-primary/20" data-testid={`pixel-${px.pixel_id}`}>
                    <div className="min-w-0 pr-3">
                      <p className="text-sm font-semibold text-foreground">{px.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{pixelTypeLabels[px.type] || px.type} — {px.pixel_code?.slice(0, 30)}…</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={() => handleEditPixel(px)}
                        className="rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/5 p-2 transition-colors"
                        data-testid={`edit-pixel-${px.pixel_id}`}
                      >
                        <PencilSimple size={18} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeletePixel(px.pixel_id)}
                        className="rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/5 p-2 transition-colors"
                        data-testid={`delete-pixel-${px.pixel_id}`}
                      >
                        <Trash size={18} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground rounded-xl border border-dashed border-border/80 bg-muted/20 px-4 py-8 text-center">No pixels yet. Add one to retarget visitors.</p>
            )}
          </div>
        </TabsContent>

        {/* UTM Presets tab */}
        <TabsContent value="utm">
          <div className="surface-card ambient-shadow border-border/90 rounded-xl p-6 md:p-8">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
              <div>
                <h3 className="font-display text-lg font-bold text-foreground tracking-tight">UTM presets</h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-xl">Reusable UTM sets for faster link creation.</p>
              </div>
              <Button onClick={() => setShowAddUTM(true)} className="rounded-lg bg-primary text-primary-foreground hover:opacity-95 text-sm gap-1.5 h-10 font-semibold shrink-0" data-testid="add-utm-btn">
                <Plus size={16} weight="bold" /> Add preset
              </Button>
            </div>
            {utmPresets.length === 0 && (
              <div className="mb-6 rounded-xl border border-dashed border-primary/30 bg-primary/[0.03] px-5 py-4">
                <p className="text-sm font-semibold text-foreground mb-1">New to UTM parameters?</p>
                <p className="text-xs text-muted-foreground mb-3">
                  UTM tags help you track where your traffic comes from in Google Analytics and similar tools. Load two starter presets to see how they work.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                  {[
                    { name: 'Email newsletter', source: 'newsletter', medium: 'email', campaign: 'weekly-digest', term: '', content: 'header-cta' },
                    { name: 'Twitter / X organic', source: 'twitter', medium: 'social', campaign: 'organic', term: '', content: '' },
                  ].map((ex) => (
                    <div key={ex.name} className="rounded-lg border border-border/80 bg-white p-3">
                      <p className="text-xs font-semibold text-foreground mb-1">{ex.name}</p>
                      <p className="text-[10px] font-mono text-muted-foreground leading-relaxed">
                        utm_source=<span className="text-primary">{ex.source}</span><br />
                        utm_medium=<span className="text-primary">{ex.medium}</span><br />
                        utm_campaign=<span className="text-primary">{ex.campaign}</span>
                        {ex.content && <><br />utm_content=<span className="text-primary">{ex.content}</span></>}
                      </p>
                    </div>
                  ))}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-lg border-primary/30 text-primary hover:bg-primary/10 text-xs h-8 px-3 font-semibold"
                  onClick={async () => {
                    const examples = [
                      { name: 'Email newsletter', source: 'newsletter', medium: 'email', campaign: 'weekly-digest', term: '', content: 'header-cta' },
                      { name: 'Twitter / X organic', source: 'twitter', medium: 'social', campaign: 'organic', term: '', content: '' },
                    ];
                    try {
                      await Promise.all(examples.map(e => api.post(`/workspaces/${workspace.workspace_id}/utm-presets`, e)));
                      fetchUTMPresets();
                      toast.success('Example templates loaded');
                    } catch {
                      toast.error('Failed to load examples');
                    }
                  }}
                >
                  Load example templates
                </Button>
              </div>
            )}
            {utmPresets.length > 0 ? (
              <div className="space-y-3">
                {utmPresets.map((preset) => (
                  <div key={preset.preset_id} className="flex items-center justify-between rounded-xl border border-border/80 bg-white p-4 transition-colors hover:border-primary/20" data-testid={`utm-${preset.preset_id}`}>
                    <div className="min-w-0 pr-3">
                      <p className="text-sm font-semibold text-foreground">{preset.name}</p>
                      <p className="text-xs text-muted-foreground font-mono mt-0.5">
                        {[preset.source && `src=${preset.source}`, preset.medium && `med=${preset.medium}`, preset.campaign && `camp=${preset.campaign}`].filter(Boolean).join(', ')}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={() => handleEditUTM(preset)}
                        className="rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/5 p-2 transition-colors"
                        data-testid={`edit-utm-${preset.preset_id}`}
                      >
                        <PencilSimple size={18} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteUTM(preset.preset_id)}
                        className="rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/5 p-2 transition-colors"
                        data-testid={`delete-utm-${preset.preset_id}`}
                      >
                        <Trash size={18} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground rounded-xl border border-dashed border-border/80 bg-muted/20 px-4 py-8 text-center">No UTM presets yet.</p>
            )}
          </div>
        </TabsContent>

        {/* CTA Overlays tab */}
        <TabsContent value="ctas">
          <div className="surface-card ambient-shadow border-border/90 rounded-xl p-6 md:p-8">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
              <div>
                <h3 className="font-display text-lg font-bold text-foreground tracking-tight">CTA overlays</h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-xl">Branded banners on destination pages after a click.</p>
              </div>
              <Button onClick={() => setShowAddCTA(true)} className="rounded-lg bg-primary text-primary-foreground hover:opacity-95 text-sm gap-1.5 h-10 font-semibold shrink-0" data-testid="add-cta-btn">
                <Plus size={16} weight="bold" /> Create CTA
              </Button>
            </div>
            {ctas.length > 0 ? (
              <div className="space-y-4">
                {ctas.map((cta) => {
                  const stats = ctaStats.find((s) => s.cta_id === cta.cta_id);
                  return (
                    <div key={cta.cta_id} className="rounded-xl border border-border/80 bg-white p-4 transition-colors hover:border-primary/20" data-testid={`cta-${cta.cta_id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-foreground">{cta.name}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{cta.headline} — {cta.position.replace('_', ' ')}</p>
                          {stats && (
                            <div className="flex flex-wrap gap-4 mt-3 text-xs text-muted-foreground">
                              <span className="text-primary font-medium"><Eye size={12} className="inline mr-1 align-text-bottom" weight="bold" />{stats.impressions} impressions</span>
                              <span>{stats.clicks} clicks</span>
                              <span>{stats.ctr}% CTR</span>
                            </div>
                          )}
                        </div>
                        <div
                          className="w-28 h-12 rounded-lg flex items-center justify-center text-[10px] font-semibold shrink-0 shadow-sm border border-white/10"
                          style={{ background: cta.style?.bg_color || '#131b2e', color: cta.style?.text_color || '#fff' }}
                        >
                          {cta.button_label}
                        </div>
                        <button type="button" onClick={() => handleDeleteCTA(cta.cta_id)} className="rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/5 p-2 shrink-0" data-testid={`delete-cta-${cta.cta_id}`}>
                          <Trash size={18} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground rounded-xl border border-dashed border-border/80 bg-muted/20 px-4 py-8 text-center">No CTA overlays yet.</p>
            )}
          </div>
        </TabsContent>

        {/* Custom Domains tab */}
        <TabsContent value="domains">
          <div className="surface-card ambient-shadow border-border/90 rounded-xl p-6 md:p-8">
            <div className="mb-6">
              <h3 className="font-display text-lg font-bold text-foreground tracking-tight">Custom domains</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-xl">Use your own domain for short links.</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 mb-6">
              <input
                value={domainInput}
                onChange={(e) => setDomainInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddDomain()}
                placeholder="e.g. links.yourbrand.com"
                className="flex-1 rounded-lg border border-border bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-shadow"
                data-testid="domain-input"
              />
              <Button
                onClick={handleAddDomain}
                className="rounded-lg bg-primary text-primary-foreground hover:opacity-95 text-sm gap-1.5 h-11 font-semibold sm:shrink-0"
                data-testid="add-domain-btn"
              >
                <Plus size={16} weight="bold" /> Add domain
              </Button>
            </div>

            {lastAddedDomain && (
              <div className="rounded-xl border border-primary/20 bg-primary/[0.04] p-5 mb-6" data-testid="cname-instructions">
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary mb-2">DNS setup</p>
                <p className="text-sm text-foreground">
                  Point CNAME to{' '}
                  <span className="font-mono font-semibold text-primary">{lastAddedDomain.cname_target || 'cname.linkflow.app'}</span>
                </p>
                <p className="text-xs text-muted-foreground mt-2">Propagation can take up to 48 hours.</p>
              </div>
            )}

            {domains.length > 0 ? (
              <div className="space-y-3">
                {domains.map((d) => (
                  <div key={d.domain_id} className="flex items-center justify-between rounded-xl border border-border/80 bg-white p-4 gap-3" data-testid={`domain-${d.domain_id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <Globe size={18} className="text-primary shrink-0" weight="duotone" />
                      <p className="text-sm font-semibold text-foreground truncate">{d.domain}</p>
                      {d.verified ? (
                        <span className="text-xs font-semibold px-2.5 py-1 rounded-full shrink-0 bg-emerald-50 text-emerald-700" data-testid={`domain-verified-badge-${d.domain_id}`}>
                          Verified
                        </span>
                      ) : (
                        <span className="text-xs font-semibold px-2.5 py-1 rounded-full shrink-0 bg-amber-50 text-amber-800" data-testid={`domain-pending-badge-${d.domain_id}`}>
                          Pending DNS
                        </span>
                      )}
                      {d.verified && (
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full shrink-0 ${
                          d.ssl_status === 'active' ? 'bg-emerald-50 text-emerald-700' :
                          d.ssl_status === 'pending' ? 'bg-blue-50 text-blue-700' :
                          d.ssl_status === 'error' ? 'bg-red-50 text-red-700' :
                          'bg-muted text-muted-foreground'
                        }`}>
                          {d.ssl_status === 'active' ? 'SSL Active' :
                           d.ssl_status === 'pending' ? 'SSL Pending' :
                           d.ssl_status === 'error' ? 'SSL Error' : 'No SSL'}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {!d.verified && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleVerifyDomain(d.domain_id)}
                          className="rounded-lg text-xs h-8 px-3 border-border"
                          data-testid={`verify-domain-${d.domain_id}`}
                        >
                          Verify
                        </Button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleDeleteDomain(d.domain_id)}
                        className="rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/5 p-2"
                        data-testid={`delete-domain-${d.domain_id}`}
                      >
                        <Trash size={18} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground rounded-xl border border-dashed border-border/80 bg-muted/20 px-4 py-8 text-center" data-testid="no-domains-msg">No custom domains yet.</p>
            )}
          </div>
        </TabsContent>

        {/* Webhooks tab */}
        <TabsContent value="webhooks">
          <div className="surface-card ambient-shadow border-border/90 rounded-xl p-6 md:p-8">
            <div className="mb-6">
              <h3 className="font-display text-lg font-bold text-foreground tracking-tight">Webhooks</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-xl">HTTP POST notifications for workspace events.</p>
            </div>

            <div className="rounded-xl border border-border/80 bg-muted/20 p-5 mb-8">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-4">Register webhook</p>
              <div className="space-y-4">
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-2 block">Endpoint URL</label>
                  <input
                    value={webhookForm.url}
                    onChange={(e) => setWebhookForm({ ...webhookForm, url: e.target.value })}
                    placeholder="https://your-server.com/webhook"
                    className="w-full rounded-lg border border-border bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-shadow"
                    data-testid="webhook-url-input"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-2 block">Events</label>
                  <div className="flex flex-wrap gap-4">
                    {WEBHOOK_EVENTS.map((event) => (
                      <label key={event} className="flex items-center gap-2 cursor-pointer" data-testid={`webhook-event-${event}`}>
                        <input
                          type="checkbox"
                          checked={webhookForm.events.includes(event)}
                          onChange={() => toggleWebhookEvent(event)}
                          className="h-4 w-4 rounded border-border accent-primary"
                          data-testid={`webhook-event-checkbox-${event}`}
                        />
                        <span className="text-xs text-foreground font-mono">{event}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <Button
                  onClick={handleAddWebhook}
                  disabled={webhookLoading}
                  className="rounded-lg bg-primary text-primary-foreground hover:opacity-95 text-sm gap-1.5 h-10 font-semibold"
                  data-testid="add-webhook-btn"
                >
                  <Plus size={16} weight="bold" /> Add webhook
                </Button>
              </div>
            </div>

            {webhooks.length > 0 ? (
              <div className="space-y-3">
                {webhooks.map((wh) => (
                  <div key={wh.webhook_id} className="rounded-xl border border-border/80 bg-white p-4" data-testid={`webhook-${wh.webhook_id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-foreground truncate" data-testid={`webhook-url-${wh.webhook_id}`}>{wh.url}</p>
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {(wh.events || []).map((ev) => (
                            <span
                              key={ev}
                              className="text-[11px] font-mono px-2 py-1 rounded-md bg-muted text-foreground"
                              data-testid={`webhook-event-badge-${wh.webhook_id}-${ev}`}
                            >
                              {ev}
                            </span>
                          ))}
                        </div>
                        {wh.secret && (
                          <p className="text-xs text-muted-foreground mt-2 font-mono" data-testid={`webhook-secret-${wh.webhook_id}`}>
                            Secret: {wh.secret.slice(0, 8)}••••••••
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleTestWebhook(wh.webhook_id)}
                          className="rounded-lg text-xs h-8 px-3 gap-1 border-border"
                          data-testid={`test-webhook-${wh.webhook_id}`}
                        >
                          <Play size={12} weight="fill" /> Test
                        </Button>
                        <button
                          type="button"
                          onClick={() => handleDeleteWebhook(wh.webhook_id)}
                          className="rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/5 p-2"
                          data-testid={`delete-webhook-${wh.webhook_id}`}
                        >
                          <Trash size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground rounded-xl border border-dashed border-border/80 bg-muted/20 px-4 py-8 text-center" data-testid="no-webhooks-msg">No webhooks yet.</p>
            )}
          </div>
        </TabsContent>

        {/* Audit Log tab */}
        <TabsContent value="audit">
          <div className="space-y-4 max-w-4xl">
            <div>
              <h2 className="text-base font-semibold">Audit Log</h2>
              <p className="text-sm text-muted-foreground mt-0.5">Immutable record of all workspace actions by your team.</p>
            </div>
            {auditLoading && auditEvents.length === 0 ? (
              <p className="text-sm text-muted-foreground">Loading...</p>
            ) : auditEvents.length > 0 ? (
              <>
                <div className="surface-card ambient-shadow border-border/90 rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border/60 bg-muted/30">
                        <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Time</th>
                        <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Actor</th>
                        <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Action</th>
                        <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Resource</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/40">
                      {auditEvents.map(evt => (
                        <tr key={evt.event_id} className="hover:bg-muted/20 transition-colors">
                          <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                            {new Date(evt.created_at).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-xs">
                            {evt.users?.name || evt.users?.email || 'System'}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-semibold ${
                              evt.action.includes('deleted') || evt.action.includes('revoked') ? 'bg-red-50 text-red-700' :
                              evt.action.includes('created') || evt.action.includes('invited') ? 'bg-emerald-50 text-emerald-700' :
                              'bg-blue-50 text-blue-700'
                            }`}>
                              {evt.action}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs text-muted-foreground font-mono">
                            {evt.resource_type}{evt.resource_id ? ` · ${evt.resource_id.slice(0, 16)}` : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {auditHasMore && (
                  <div className="text-center">
                    <Button variant="outline" size="sm" onClick={() => fetchAuditLog(auditCursor, true)} disabled={auditLoading}>
                      {auditLoading ? 'Loading...' : 'Load more'}
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground rounded-xl border border-dashed border-border/80 bg-muted/20 px-4 py-8 text-center">No audit events yet. Actions taken by your team will appear here.</p>
            )}
          </div>
        </TabsContent>

        {/* API Keys tab */}
        <TabsContent value="api">
          <div className="space-y-6 max-w-3xl">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold">API Keys</h2>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Use API keys to access LinkFlow programmatically.{' '}
                  <a href="/docs" target="_blank" rel="noopener noreferrer" className="text-primary underline-offset-4 hover:underline">
                    View API docs <ArrowSquareOut size={11} className="inline" />
                  </a>
                </p>
              </div>
              <Button size="sm" onClick={() => { setShowCreateKey(true); setCreatedKey(null); }}>
                <Plus size={14} className="mr-1.5" /> Generate key
              </Button>
            </div>

            {/* New key created — show once */}
            {createdKey && (
              <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 space-y-3">
                <p className="text-sm font-semibold text-amber-600 dark:text-amber-400">Save this key — it won't be shown again.</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs bg-muted rounded-lg px-3 py-2 font-mono break-all">{createdKey}</code>
                  <Button variant="outline" size="sm" onClick={copyKey} className="shrink-0">
                    {keyCopied ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
                  </Button>
                </div>
              </div>
            )}

            {/* Create key form */}
            {showCreateKey && !createdKey && (
              <div className="surface-card ambient-shadow border-border/90 rounded-xl p-6 space-y-4">
                <h3 className="text-sm font-semibold">New API key</h3>
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-1.5 block">Name</label>
                  <input
                    value={newKeyName}
                    onChange={e => setNewKeyName(e.target.value)}
                    placeholder="e.g. Production, CI/CD"
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-2 block">Scopes</label>
                  <div className="space-y-2">
                    {[
                      { id: 'links:read', label: 'Links — Read', desc: 'List and view links' },
                      { id: 'links:write', label: 'Links — Write', desc: 'Create, update, delete links' },
                      { id: 'analytics:read', label: 'Analytics — Read', desc: 'View click analytics' },
                      { id: 'webhooks:write', label: 'Webhooks — Write', desc: 'Manage webhook endpoints' },
                    ].map(scope => (
                      <label key={scope.id} className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newKeyScopes.includes(scope.id)}
                          onChange={e => setNewKeyScopes(prev =>
                            e.target.checked ? [...prev, scope.id] : prev.filter(s => s !== scope.id)
                          )}
                          className="mt-0.5 accent-primary"
                        />
                        <span className="text-sm">
                          <span className="font-medium">{scope.label}</span>
                          <span className="text-muted-foreground ml-2">{scope.desc}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2 pt-1">
                  <Button size="sm" onClick={handleCreateApiKey}>Generate</Button>
                  <Button size="sm" variant="outline" onClick={() => { setShowCreateKey(false); setNewKeyName(''); }}>Cancel</Button>
                </div>
              </div>
            )}

            {/* Keys list */}
            {apiKeysLoading ? (
              <p className="text-sm text-muted-foreground">Loading...</p>
            ) : apiKeys.length > 0 ? (
              <div className="surface-card ambient-shadow border-border/90 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border/60 bg-muted/30">
                      <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Name</th>
                      <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Key prefix</th>
                      <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Scopes</th>
                      <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Last used</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/40">
                    {apiKeys.map(key => (
                      <tr key={key.key_id} className="hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-3 font-medium">{key.name}</td>
                        <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{key.key_id}</td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {(key.scopes || []).map(s => (
                              <span key={s} className="inline-flex items-center rounded-md bg-primary/10 text-primary px-1.5 py-0.5 text-[10px] font-medium">{s}</span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground text-xs">
                          {key.last_used_at ? new Date(key.last_used_at).toLocaleDateString() : 'Never'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive hover:bg-destructive/10 h-7 px-2" onClick={() => handleRevokeApiKey(key.key_id)}>
                            <Trash size={13} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground rounded-xl border border-dashed border-border/80 bg-muted/20 px-4 py-8 text-center">No API keys yet. Generate one above to get started.</p>
            )}
          </div>
        </TabsContent>

        {/* Conversions tab */}
        <TabsContent value="conversions">
          <div className="space-y-6 max-w-2xl">
            <div>
              <h2 className="text-lg font-bold text-foreground mb-1">Server-Side Conversions</h2>
              <p className="text-sm text-muted-foreground">Send conversion events server-to-server to Meta and Google without relying on browser pixels.</p>
            </div>

            {capiLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-8">
                <CircleNotch size={16} className="animate-spin" /> Loading...
              </div>
            ) : (
              <div className="space-y-6">
                {/* Meta CAPI */}
                <div className="surface-card ambient-shadow border-border/90 rounded-xl p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center text-white text-xs font-bold">M</div>
                      <div>
                        <p className="text-sm font-semibold">Meta Conversions API</p>
                        <p className="text-xs text-muted-foreground">Send events to Meta without relying on the browser pixel</p>
                      </div>
                    </div>
                    <Switch
                      checked={capiForm.enabled_meta}
                      onCheckedChange={(v) => setCapiForm((p) => ({ ...p, enabled_meta: v }))}
                      data-testid="capi-meta-toggle"
                    />
                  </div>
                  {capiForm.enabled_meta && (
                    <div className="space-y-3 pt-2 border-t border-border/60">
                      <div>
                        <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-1.5 block">Pixel ID</label>
                        <input
                          value={capiForm.meta_pixel_id}
                          onChange={(e) => setCapiForm((p) => ({ ...p, meta_pixel_id: e.target.value }))}
                          placeholder="e.g. 1234567890"
                          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                          data-testid="capi-meta-pixel-id"
                        />
                      </div>
                      <div>
                        <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-1.5 block">Access Token</label>
                        <input
                          value={capiForm.meta_access_token}
                          onChange={(e) => setCapiForm((p) => ({ ...p, meta_access_token: e.target.value }))}
                          placeholder={capiSettings?.meta_access_token_set ? 'Stored — enter new value to replace' : 'EAAxxxxx...'}
                          type="password"
                          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                          data-testid="capi-meta-token"
                        />
                      </div>
                      <div>
                        <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-1.5 block">Test Event Code (optional)</label>
                        <input
                          value={capiForm.meta_test_event_code}
                          onChange={(e) => setCapiForm((p) => ({ ...p, meta_test_event_code: e.target.value }))}
                          placeholder="TEST12345"
                          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                          data-testid="capi-meta-test-code"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* GA4 */}
                <div className="surface-card ambient-shadow border-border/90 rounded-xl p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-orange-500 flex items-center justify-center text-white text-xs font-bold">G4</div>
                      <div>
                        <p className="text-sm font-semibold">Google Analytics 4 (Measurement Protocol)</p>
                        <p className="text-xs text-muted-foreground">Send server-side conversion events to GA4</p>
                      </div>
                    </div>
                    <Switch
                      checked={capiForm.enabled_ga4}
                      onCheckedChange={(v) => setCapiForm((p) => ({ ...p, enabled_ga4: v }))}
                      data-testid="capi-ga4-toggle"
                    />
                  </div>
                  {capiForm.enabled_ga4 && (
                    <div className="space-y-3 pt-2 border-t border-border/60">
                      <div>
                        <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-1.5 block">Measurement ID</label>
                        <input
                          value={capiForm.ga4_measurement_id}
                          onChange={(e) => setCapiForm((p) => ({ ...p, ga4_measurement_id: e.target.value }))}
                          placeholder="G-XXXXXXXXXX"
                          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                          data-testid="capi-ga4-measurement-id"
                        />
                      </div>
                      <div>
                        <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-1.5 block">API Secret</label>
                        <input
                          value={capiForm.ga4_api_secret}
                          onChange={(e) => setCapiForm((p) => ({ ...p, ga4_api_secret: e.target.value }))}
                          placeholder={capiSettings?.ga4_api_secret_set ? 'Stored — enter new value to replace' : 'your-api-secret'}
                          type="password"
                          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                          data-testid="capi-ga4-secret"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Save + Test */}
                <div className="flex items-center gap-3">
                  <Button onClick={handleSaveCapiSettings} disabled={capiSaving} className="rounded-lg bg-primary text-primary-foreground hover:opacity-95 font-semibold" data-testid="save-capi-btn">
                    {capiSaving ? <CircleNotch size={14} className="animate-spin mr-1.5" /> : null}
                    Save settings
                  </Button>
                  {(capiSettings?.enabled_meta || capiSettings?.enabled_ga4) && (
                    <Button variant="outline" onClick={handleCapiTest} disabled={capiTestLoading} className="rounded-lg border-border" data-testid="capi-test-btn">
                      {capiTestLoading ? <CircleNotch size={14} className="animate-spin mr-1.5" /> : <Play size={14} className="mr-1.5" />}
                      Send test event
                    </Button>
                  )}
                </div>

                {/* Test result */}
                {capiTestResult && (
                  <div className="rounded-xl border border-border/80 bg-muted/20 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-2">Test result</p>
                    <pre className="text-xs text-foreground whitespace-pre-wrap break-all">{JSON.stringify(capiTestResult, null, 2)}</pre>
                  </div>
                )}

                {/* Integration snippet */}
                <div className="surface-card ambient-shadow border-border/90 rounded-xl p-6 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold">Integration snippet</p>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={() => {
                        const snippet = `// Call this after a successful conversion on your site
fetch("https://erxwwlgbubhwukwssdbh.supabase.co/functions/v1/capi/event", {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({
    workspace_id: "${workspace?.workspace_id || 'YOUR_WORKSPACE_ID'}",
    event_name: "Purchase",
    value: 99.00,
    currency: "USD",
    email: customer.email,   // hashed server-side automatically
    link_id: linkId,         // optional — ties conversion to a link
    event_source_url: window.location.href,
  }),
});`;
                        navigator.clipboard.writeText(snippet);
                        setCapiSnippetCopied(true);
                        setTimeout(() => setCapiSnippetCopied(false), 2000);
                      }}
                    >
                      {capiSnippetCopied ? <Check size={12} className="text-green-500 mr-1" /> : <Copy size={12} className="mr-1" />}
                      {capiSnippetCopied ? 'Copied' : 'Copy'}
                    </Button>
                  </div>
                  <pre className="text-xs bg-muted/40 rounded-lg p-4 overflow-x-auto text-foreground/80 leading-relaxed">{`fetch("/functions/v1/capi/event", {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({
    workspace_id: "${workspace?.workspace_id || 'YOUR_WORKSPACE_ID'}",
    event_name: "Purchase",
    value: 99.00,
    currency: "USD",
    email: customer.email,
    link_id: linkId,
  }),
});`}</pre>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Zapier Tab */}
        <TabsContent value="zapier" data-testid="zapier-tab-content">
          <div className="space-y-6 max-w-2xl">
            {/* Hero card */}
            <div className="rounded-2xl border border-border/80 bg-gradient-to-br from-orange-50 to-amber-50 p-6">
              <div className="flex items-start gap-4">
                <div className="grid place-items-center h-12 w-12 rounded-xl bg-orange-500/10 text-orange-600 shrink-0">
                  <Lightning size={24} weight="duotone" />
                </div>
                <div>
                  <h3 className="font-display text-base font-bold text-foreground">Connect LinkFlow to 6,000+ apps via Zapier</h3>
                  <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                    Every time a link is clicked, a new link is created, or a threshold is reached — trigger automations in Google Sheets, Slack, Notion, HubSpot, Airtable, and more.
                  </p>
                  <a
                    href="https://zapier.com/apps/webhook/integrations"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 mt-3 text-sm font-semibold text-orange-600 hover:underline"
                  >
                    Open Zapier <ArrowSquareOut size={13} />
                  </a>
                </div>
              </div>
            </div>

            {/* How it works */}
            <div className="surface-card ambient-shadow border-border/90 rounded-xl p-6">
              <h4 className="font-display text-sm font-bold text-foreground mb-4">How to connect</h4>
              <ol className="space-y-4">
                {[
                  ['Create a Zap', 'In Zapier, create a new Zap and choose "Webhooks by Zapier" as the trigger app.'],
                  ['Choose "Catch Hook"', 'Select the "Catch Hook" trigger event. Zapier will generate a unique webhook URL.'],
                  ['Paste URL in LinkFlow', 'Copy the Zapier webhook URL and add it in Settings → Webhooks. Subscribe to the events you want.'],
                  ['Test the connection', 'Use the "Send test event" button in the Webhooks tab to send a sample payload to Zapier.'],
                  ['Build your workflow', 'Add an action step in Zapier (e.g., add row to Google Sheets) and map the LinkFlow payload fields.'],
                ].map(([title, desc], i) => (
                  <li key={i} className="flex gap-4">
                    <span className="grid place-items-center h-7 w-7 rounded-full bg-primary/10 text-primary text-xs font-bold shrink-0 mt-0.5">
                      {i + 1}
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-foreground">{title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{desc}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            {/* Webhook payload reference */}
            <div className="surface-card ambient-shadow border-border/90 rounded-xl p-6 space-y-4">
              <h4 className="font-display text-sm font-bold text-foreground">Payload reference</h4>
              <p className="text-xs text-muted-foreground">
                All LinkFlow webhook events are sent as JSON POST requests signed with HMAC-SHA256. The payload shape depends on the event type.
              </p>

              {[
                {
                  event: 'link.clicked',
                  description: 'Fired on every non-bot click',
                  payload: `{
  "event": "link.clicked",
  "timestamp": "2026-05-02T10:30:00Z",
  "workspace_id": "ws_abc123",
  "data": {
    "link_id": "lnk_xyz",
    "slug": "my-campaign",
    "destination_url": "https://example.com/product",
    "total_clicks": 1042,
    "country": "United States",
    "device": "mobile",
    "referrer": "https://twitter.com"
  }
}`,
                },
                {
                  event: 'link.threshold_reached',
                  description: 'Fired when a click milestone is hit',
                  payload: `{
  "event": "link.threshold_reached",
  "timestamp": "2026-05-02T10:30:00Z",
  "workspace_id": "ws_abc123",
  "data": {
    "link_id": "lnk_xyz",
    "slug": "my-campaign",
    "threshold": 1000,
    "total_clicks": 1000
  }
}`,
                },
                {
                  event: 'link.created',
                  description: 'Fired when a new link is created',
                  payload: `{
  "event": "link.created",
  "timestamp": "2026-05-02T10:30:00Z",
  "workspace_id": "ws_abc123",
  "data": {
    "link_id": "lnk_xyz",
    "slug": "my-campaign",
    "destination_url": "https://example.com/product",
    "title": "Summer sale",
    "tags": ["sale", "email"]
  }
}`,
                },
              ].map(({ event, description, payload }) => (
                <div key={event} className="rounded-xl border border-border/80 overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-2.5 bg-muted/30 border-b border-border/80">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded-md">{event}</span>
                      <span className="text-xs text-muted-foreground">{description}</span>
                    </div>
                  </div>
                  <pre className="text-[11px] text-foreground/80 p-4 overflow-x-auto leading-relaxed bg-white">{payload}</pre>
                </div>
              ))}
            </div>

            {/* Quick link to webhooks tab */}
            <div className="flex items-center gap-3 rounded-xl border border-primary/20 bg-primary/[0.03] px-5 py-4">
              <LinkIcon size={18} className="text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground">Manage your webhook endpoints</p>
                <p className="text-xs text-muted-foreground mt-0.5">Add and test your Zapier catch-hook URLs in the Webhooks tab.</p>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="rounded-xl border-border shrink-0"
                onClick={() => {
                  // Switch to webhooks tab
                  document.querySelector('[value="webhooks"]')?.click();
                }}
              >
                Go to Webhooks <ArrowSquareOut size={12} className="ml-1" />
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Pixel Dialog */}
      <Dialog open={showAddPixel} onOpenChange={setShowAddPixel}>
        <DialogContent className="sm:max-w-md rounded-xl border border-border">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold tracking-tight">Add retargeting pixel</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-2 block">Name</label>
              <input
                value={pixelForm.name}
                onChange={(e) => setPixelForm({ ...pixelForm, name: e.target.value })}
                placeholder="e.g. Facebook Retargeting"
                className="w-full rounded-lg border border-border bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-shadow"
                data-testid="pixel-name-input"
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-2 block">Type</label>
              <Select value={pixelForm.type} onValueChange={(v) => setPixelForm({ ...pixelForm, type: v })}>
                <SelectTrigger className="bg-white rounded-lg border-border" data-testid="pixel-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="meta">Meta Pixel</SelectItem>
                  <SelectItem value="google">Google Ads</SelectItem>
                  <SelectItem value="linkedin">LinkedIn Insight</SelectItem>
                  <SelectItem value="tiktok">TikTok Pixel</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-2 block">Pixel ID / Code</label>
              <input
                value={pixelForm.pixel_code}
                onChange={(e) => setPixelForm({ ...pixelForm, pixel_code: e.target.value })}
                placeholder="e.g. 1234567890"
                className="w-full rounded-lg border border-border bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-shadow"
                data-testid="pixel-code-input"
              />
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setShowAddPixel(false)} className="rounded-lg border-border">Cancel</Button>
              <Button onClick={handleAddPixel} className="rounded-lg bg-primary text-primary-foreground hover:opacity-95 font-semibold" data-testid="save-pixel-btn">Add pixel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Pixel Dialog */}
      <Dialog open={!!editingPixel} onOpenChange={(open) => { if (!open) setEditingPixel(null); }}>
        <DialogContent className="sm:max-w-md rounded-xl border border-border">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold tracking-tight">Edit retargeting pixel</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-2 block">Name</label>
              <input
                value={editPixelForm.name}
                onChange={(e) => setEditPixelForm({ ...editPixelForm, name: e.target.value })}
                className="w-full rounded-lg border border-border bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-shadow"
                data-testid="edit-pixel-name-input"
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-2 block">Pixel ID / Code</label>
              <input
                value={editPixelForm.pixel_code}
                onChange={(e) => setEditPixelForm({ ...editPixelForm, pixel_code: e.target.value })}
                className="w-full rounded-lg border border-border bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-shadow"
                data-testid="edit-pixel-code-input"
              />
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setEditingPixel(null)} className="rounded-lg border-border">Cancel</Button>
              <Button onClick={handleSaveEditPixel} className="rounded-lg bg-primary text-primary-foreground hover:opacity-95 font-semibold" data-testid="save-edit-pixel-btn">Save changes</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add UTM Preset Dialog */}
      <Dialog open={showAddUTM} onOpenChange={setShowAddUTM}>
        <DialogContent className="sm:max-w-md rounded-xl border border-border">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold tracking-tight">Create UTM preset</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            {[
              { key: 'name', label: 'Preset Name', placeholder: 'e.g. Q1 Email Campaign' },
              { key: 'source', label: 'Source', placeholder: 'e.g. newsletter' },
              { key: 'medium', label: 'Medium', placeholder: 'e.g. email' },
              { key: 'campaign', label: 'Campaign', placeholder: 'e.g. spring-sale-2026' },
              { key: 'term', label: 'Term (optional)', placeholder: 'e.g. discount' },
              { key: 'content', label: 'Content (optional)', placeholder: 'e.g. header-cta' },
            ].map((field) => (
              <div key={field.key}>
                <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-2 block">{field.label}</label>
                <input
                  value={utmForm[field.key]}
                  onChange={(e) => setUtmForm({ ...utmForm, [field.key]: e.target.value })}
                  placeholder={field.placeholder}
                  className="w-full rounded-lg border border-border bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-shadow"
                  data-testid={`utm-${field.key}-input`}
                />
              </div>
            ))}
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setShowAddUTM(false)} className="rounded-lg border-border">Cancel</Button>
              <Button onClick={handleAddUTM} className="rounded-lg bg-primary text-primary-foreground hover:opacity-95 font-semibold" data-testid="save-utm-btn">Create preset</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit UTM Preset Dialog */}
      <Dialog open={!!editingUTM} onOpenChange={(open) => { if (!open) setEditingUTM(null); }}>
        <DialogContent className="sm:max-w-md rounded-xl border border-border">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold tracking-tight">Edit UTM preset</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            {[
              { key: 'name', label: 'Preset Name' },
              { key: 'source', label: 'Source' },
              { key: 'medium', label: 'Medium' },
              { key: 'campaign', label: 'Campaign' },
              { key: 'term', label: 'Term (optional)' },
              { key: 'content', label: 'Content (optional)' },
            ].map((field) => (
              <div key={field.key}>
                <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-2 block">{field.label}</label>
                <input
                  value={editUTMForm[field.key]}
                  onChange={(e) => setEditUTMForm({ ...editUTMForm, [field.key]: e.target.value })}
                  className="w-full rounded-lg border border-border bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-shadow"
                  data-testid={`edit-utm-${field.key}-input`}
                />
              </div>
            ))}
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setEditingUTM(null)} className="rounded-lg border-border">Cancel</Button>
              <Button onClick={handleSaveEditUTM} className="rounded-lg bg-primary text-primary-foreground hover:opacity-95 font-semibold" data-testid="save-edit-utm-btn">Save changes</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add CTA Dialog */}
      <Dialog open={showAddCTA} onOpenChange={setShowAddCTA}>
        <DialogContent className="sm:max-w-lg rounded-xl border border-border max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold tracking-tight">Create CTA overlay</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {/* AI Generate */}
            <div className="rounded-xl border border-primary/20 bg-primary/[0.03] px-4 py-3 flex items-center gap-3">
              <Sparkle size={16} weight="duotone" className="text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-foreground">Generate with AI</p>
                <p className="text-xs text-muted-foreground">Enter the button URL below, then let Claude write the copy for you.</p>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="shrink-0 border-primary/30 text-primary hover:bg-primary/10 text-xs h-7 px-3"
                onClick={handleGenerateCtaWithAi}
                disabled={ctaAiLoading}
                data-testid="cta-ai-generate-btn"
              >
                {ctaAiLoading ? <CircleNotch size={12} className="animate-spin mr-1" /> : <Sparkle size={12} className="mr-1" />}
                {ctaAiLoading ? 'Generating…' : 'Generate'}
              </Button>
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-2 block">Name</label>
              <input value={ctaForm.name} onChange={(e) => setCtaForm({ ...ctaForm, name: e.target.value })} placeholder="e.g. Spring Sale Banner" className="w-full rounded-lg border border-border bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-shadow" data-testid="cta-name-input" />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-2 block">Headline</label>
              <input value={ctaForm.headline} onChange={(e) => setCtaForm({ ...ctaForm, headline: e.target.value })} placeholder="e.g. Spring Sale — 40% Off!" className="w-full rounded-lg border border-border bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-shadow" data-testid="cta-headline-input" />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-2 block">Body Text (optional)</label>
              <input value={ctaForm.body_text} onChange={(e) => setCtaForm({ ...ctaForm, body_text: e.target.value })} placeholder="Short description" className="w-full rounded-lg border border-border bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-shadow" data-testid="cta-body-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-2 block">Button Label</label>
                <input value={ctaForm.button_label} onChange={(e) => setCtaForm({ ...ctaForm, button_label: e.target.value })} className="w-full rounded-lg border border-border bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-shadow" data-testid="cta-btn-label-input" />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-2 block">Button URL</label>
                <input value={ctaForm.button_url} onChange={(e) => setCtaForm({ ...ctaForm, button_url: e.target.value })} placeholder="https://..." className="w-full rounded-lg border border-border bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-shadow" data-testid="cta-btn-url-input" />
              </div>
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-2 block">Position</label>
              <Select value={ctaForm.position} onValueChange={(v) => setCtaForm({ ...ctaForm, position: v })}>
                <SelectTrigger className="bg-white rounded-lg border-border" data-testid="cta-position-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="top_bar">Top Bar</SelectItem>
                  <SelectItem value="bottom_bar">Bottom Bar</SelectItem>
                  <SelectItem value="bottom_right">Bottom-Right Popup</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-2 block">Colors</label>
              <div className="grid grid-cols-4 gap-3">
                {[
                  { key: 'bg_color', label: 'Background' },
                  { key: 'text_color', label: 'Text' },
                  { key: 'button_color', label: 'Button' },
                  { key: 'button_text_color', label: 'Btn Text' },
                ].map((c) => (
                  <div key={c.key} className="text-center">
                    <input
                      type="color"
                      value={ctaForm.style[c.key]}
                      onChange={(e) => setCtaForm({ ...ctaForm, style: { ...ctaForm.style, [c.key]: e.target.value } })}
                      className="w-8 h-8 rounded-md border border-border cursor-pointer mx-auto block"
                      data-testid={`cta-color-${c.key}`}
                    />
                    <span className="text-[10px] text-muted-foreground mt-0.5 block">{c.label}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* Live Preview */}
            {ctaForm.headline && (
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-2 block">Preview</label>
                <div
                  className="flex items-center gap-3 p-3 text-sm"
                  style={{ background: ctaForm.style.bg_color, color: ctaForm.style.text_color }}
                  data-testid="cta-preview"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm">{ctaForm.headline}</div>
                    {ctaForm.body_text && <div className="text-xs opacity-80 mt-0.5">{ctaForm.body_text}</div>}
                  </div>
                  <span
                    className="px-3 py-1.5 text-xs font-semibold shrink-0"
                    style={{ background: ctaForm.style.button_color, color: ctaForm.style.button_text_color }}
                  >
                    {ctaForm.button_label}
                  </span>
                  <span style={{ opacity: 0.5 }}>&times;</span>
                </div>
              </div>
            )}
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setShowAddCTA(false)} className="rounded-lg border-border">Cancel</Button>
              <Button onClick={handleAddCTA} className="rounded-lg bg-primary text-primary-foreground hover:opacity-95 font-semibold" data-testid="save-cta-btn">Create CTA</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
